const CSV_MIME_TYPE = 'text/csv;charset=utf-8';
const CSV_BOM = '\uFEFF';

interface GenericInvoiceCsvSource {
  [key: string]: any;
  cfdixml?: string;
  settlement?: { name?: string; type?: unknown; is_commercial?: boolean };
  settlement_name?: string;
  building?: { name?: string };
  building_name?: string;
  unit?: { name?: string };
  unit_name?: string;
  property_type?: unknown;
  customer_type?: unknown;
  settlement_type?: unknown;
  is_commercial?: boolean;
  serie?: string;
  folio?: string;
  fecha?: string;
  uuid?: string;
}

export async function withCsvReportTitle(csv: Blob, reportTitle: string): Promise<Blob> {
  const content = (await csv.text()).replace(/^\uFEFF/, '');
  const lineBreak = content.includes('\r\n') ? '\r\n' : '\n';
  const lines = content.split(/\r?\n/);

  if (lines[0]?.toLocaleLowerCase().startsWith('reporte de facturas')) {
    lines[0] = reportTitle;
  } else {
    lines.unshift(reportTitle, '');
  }

  return new Blob([CSV_BOM, lines.join(lineBreak)], { type: CSV_MIME_TYPE });
}

export function buildGenericInvoicesCsv(invoices: GenericInvoiceCsvSource[], reportTitle: string): Blob {
  const headers = [
    'Condominio/comercial',
    'Torre',
    'Unidad',
    'Tipo',
    'Serie',
    'Folio',
    'Fecha',
    'Folio fiscal',
    'RFC receptor',
    'Razón social',
    'Subtotal',
    'Total'
  ];

  const rows = invoices.map(invoice => {
    const xml = readInvoiceXml(invoice.cfdixml);

    return [
      invoice.settlement?.name ?? invoice.settlement_name ?? '',
      invoice.building?.name ?? invoice.building_name ?? '',
      invoice.unit?.name ?? invoice.unit_name ?? '',
      getPropertyType(invoice),
      xml.main?.getAttribute('Serie') ?? invoice.serie ?? '',
      xml.main?.getAttribute('Folio') ?? invoice.folio ?? '',
      xml.main?.getAttribute('Fecha') ?? invoice.fecha ?? '',
      xml.stamp?.getAttribute('UUID') ?? invoice.uuid ?? '',
      xml.receiver?.getAttribute('Rfc') ?? '',
      xml.receiver?.getAttribute('Nombre') ?? '',
      xml.main?.getAttribute('SubTotal') ?? '',
      xml.main?.getAttribute('Total') ?? ''
    ].map(escapeCsvValue).join(',');
  });

  const content = [reportTitle, '', headers.join(','), ...rows].join('\n');
  return new Blob([CSV_BOM, content], { type: CSV_MIME_TYPE });
}

function readInvoiceXml(xml: unknown): {
  main: Element | null;
  receiver: Element | null;
  stamp: Element | null;
} {
  if (typeof xml !== 'string' || xml.trim() === '') {
    return { main: null, receiver: null, stamp: null };
  }

  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.querySelector('parsererror')) {
    return { main: null, receiver: null, stamp: null };
  }

  return {
    main: document.documentElement,
    receiver: document.getElementsByTagNameNS('*', 'Receptor').item(0),
    stamp: document.getElementsByTagNameNS('*', 'TimbreFiscalDigital').item(0)
  };
}

function getPropertyType(invoice: GenericInvoiceCsvSource): string {
  const value = invoice.property_type
    ?? invoice.customer_type
    ?? invoice.settlement_type
    ?? invoice.settlement?.type;

  if (typeof value === 'string') {
    const normalized = value.toLocaleLowerCase();
    if (normalized.includes('comercial')) return 'Comercial';
    if (normalized.includes('habitacional') || normalized.includes('residencial')) return 'Habitacional';
  }

  const isCommercial = invoice.is_commercial ?? invoice.settlement?.is_commercial;
  if (typeof isCommercial === 'boolean') {
    return isCommercial ? 'Comercial' : 'Habitacional';
  }

  return '';
}

function escapeCsvValue(value: unknown): string {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}
